import { useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useLanguage } from "../../context/LanguageContext";

const INTENT_LABELS = {
  id: {
    LEGAL_SEARCH: "Analisis Hukum Perpajakan",
    CALCULATION: "Kalkulasi Pajak",
    DOCUMENT_DRAFTING: "Penyusunan Dokumen Hukum",
    DISPUTE_RESPONSE: "Analisis Sengketa Pajak",
    COMPANY_KNOWLEDGE: "Pencarian Data Perusahaan",
    TECHNICAL_SUPPORT: "Bantuan Teknis CoreTax",
    EXPERT_CORRECTION: "Koreksi Expert",
    GENERAL_CHAT: "Konsultasi Umum",
    GREETING: "Sapaan",
  },
  en: {
    LEGAL_SEARCH: "Tax Legal Analysis",
    CALCULATION: "Tax Calculation",
    DOCUMENT_DRAFTING: "Legal Document Drafting",
    DISPUTE_RESPONSE: "Tax Dispute Analysis",
    COMPANY_KNOWLEDGE: "Company Knowledge Search",
    TECHNICAL_SUPPORT: "CoreTax Technical Support",
    EXPERT_CORRECTION: "Expert Correction",
    GENERAL_CHAT: "General Consultation",
    GREETING: "Greeting",
  },
};

const LEVEL_LABELS = {
  id: {
    QUICK: "Respons Cepat",
    STANDARD: "Analisis Standar",
    DEEP: "Analisis Mendalam",
    EXPERT: "Kajian Expert",
  },
  en: {
    QUICK: "Quick Response",
    STANDARD: "Standard Analysis",
    DEEP: "Deep Analysis",
    EXPERT: "Expert Review",
  },
};

function formatMs(ms) {
  if (!ms) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function StepIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 select-none pointer-events-none" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="3" fill="currentColor" opacity="0.4" />
      <circle cx="8" cy="8" r="1.5" fill="currentColor" />
    </svg>
  );
}

function ThinkingIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="shrink-0 select-none pointer-events-none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" focusable="false">
      <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * AnalysisPanel — shows structured thinking trace + pipeline steps
 * 
 * Props:
 *   analysis  — pipeline metadata (_analysis from backend)
 *   thinking  — [{label, detail}] structured thinking trace (from backend)
 *   hideSteps — if true, hide pipeline steps (for non-admin users)
 */
export default function AnalysisPanel({ analysis, thinking = [], hideSteps = false }) {
  const { t, isEnglish } = useLanguage();
  const [open, setOpen] = useState(false);
  const [thinkingOpen, setThinkingOpen] = useState(false);

  // Need at least thinking or analysis to render
  if (!thinking?.length && !analysis) return null;

  const intent = analysis?.intent || "UNKNOWN";
  const level = analysis?.reasoning_level || "STANDARD";
  const totalMs = analysis?.total_ms;
  const model = analysis?.model || "";
  const hasRlhf = analysis?.has_rlhf;
  const steps = analysis?.steps || [];
  const totalTokens = steps.reduce((s, st) => s + (st.tokens_in || 0) + (st.tokens_out || 0), 0);

  const currentIntents = isEnglish ? INTENT_LABELS.en : INTENT_LABELS.id;
  const currentLevels = isEnglish ? LEVEL_LABELS.en : LEVEL_LABELS.id;
  const intentLabel = currentIntents[intent] || intent;
  const levelLabel = currentLevels[level] || level;

  const hasThinking = thinking && thinking.length > 0;
  const summaryText = hasThinking
    ? `${isEnglish ? "Thinking" : "Berpikir"} — ${intentLabel}`
    : `${intentLabel} — ${levelLabel}`;

  return (
    <div 
      className="flex justify-start z-10 mb-1 mt-1 select-none pointer-events-auto"
      data-nosnippet="true"
      aria-hidden="true"
      style={{ userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none", msUserSelect: "none" }}
      onCopy={(e) => { e.preventDefault(); e.stopPropagation(); }}
    >
      <div 
        className="max-w-[88%] sm:max-w-[75%] w-full space-y-1 select-none"
        style={{ userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none", msUserSelect: "none" }}
      >

        {/* ── Thinking Block (Claude-like structured trace) ── */}
        {hasThinking && (
          <button
            onClick={() => setThinkingOpen(!thinkingOpen)}
            style={{ userSelect: "none", WebkitUserSelect: "none" }}
            className={`
              w-full flex items-center gap-2.5 px-3.5 py-2 select-none
              text-[13px] text-left
              rounded-xl transition-all duration-200
              ${thinkingOpen
                ? "bg-slate-800 text-slate-200 shadow-lg shadow-slate-900/20 rounded-b-none"
                : "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-500/20 border border-amber-200/60 dark:border-amber-500/30"
              }
            `}
          >
            <span className={`shrink-0 transition-colors select-none pointer-events-none ${thinkingOpen ? "text-amber-400" : "text-amber-500"}`}>
              <ThinkingIcon />
            </span>
            <span className="flex-1 truncate font-medium select-none pointer-events-none" style={{ userSelect: "none" }}>
              {thinkingOpen ? t("thinking.thinkingTitle") : t("thinking.seeThinking")}
            </span>
            <span className={`
              shrink-0 text-[11px] font-mono px-1.5 py-0.5 rounded select-none pointer-events-none
              ${thinkingOpen ? "bg-slate-700 text-slate-300" : "bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-300"}
            `}>
              {formatMs(totalMs)}
            </span>
            <ChevronDownIcon
              aria-hidden="true"
              focusable="false"
              className={`w-3.5 h-3.5 shrink-0 select-none pointer-events-none transition-transform duration-200 ${thinkingOpen ? "rotate-180" : ""}`}
            />
          </button>
        )}
        {hasThinking && thinkingOpen && (
          <div className="bg-slate-800 rounded-b-xl overflow-hidden shadow-lg shadow-slate-900/20 -mt-1">
            <div className="px-4 py-3 space-y-2.5 max-h-[400px] overflow-y-auto">
              {thinking.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span className="mt-1 text-amber-400 shrink-0">
                    <StepIcon />
                  </span>
                  <div className="flex-1 min-w-0">
                    {item.label && (
                      <span className="text-[12px] font-semibold text-slate-200 block">
                        {item.label}
                      </span>
                    )}
                    <span className="text-[12px] text-slate-400 leading-[1.6]">
                      {item.detail}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2 bg-slate-900/50 border-t border-slate-700/50 flex items-center gap-2 text-[11px] text-slate-500">
              {model && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-700 text-slate-400 font-mono">
                  {model}
                </span>
              )}
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-500/20 text-indigo-300 font-semibold">
                {levelLabel}
              </span>
            </div>
          </div>
        )}

        {/* ── Pipeline Steps (collapsed header) — admin only ── */}
        {!hideSteps && analysis && (
          <>
            <button
              onClick={() => setOpen(!open)}
              style={{ userSelect: "none", WebkitUserSelect: "none" }}
              className={`
                w-full flex items-center gap-2.5 px-3.5 py-2 select-none
                text-[13px] text-left
                rounded-xl transition-all duration-200
                ${open
                  ? "bg-slate-800 text-slate-200 shadow-lg shadow-slate-900/20"
                  : "bg-slate-100 dark:bg-slate-800/80 text-slate-500 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-white border border-transparent dark:border-slate-700/50"
                }
              `}
            >
              <span className={`shrink-0 transition-colors select-none pointer-events-none ${open ? "text-amber-400" : "text-slate-400"}`}>
                <StepIcon />
              </span>
              <span className="flex-1 truncate font-medium select-none pointer-events-none" style={{ userSelect: "none" }}>
                {hasThinking ? `${steps.length} ${t("thinking.pipelineSteps")}` : summaryText}
              </span>
              <span className={`
                shrink-0 text-[11px] font-mono px-1.5 py-0.5 rounded select-none pointer-events-none
                ${open ? "bg-slate-700 text-slate-300" : "bg-slate-200 dark:bg-slate-700/80 text-slate-500 dark:text-slate-300"}
              `}>
                {formatMs(totalMs)}
              </span>
              <ChevronDownIcon
                aria-hidden="true"
                focusable="false"
                className={`w-3.5 h-3.5 shrink-0 select-none pointer-events-none transition-transform duration-200 ${open ? "rotate-180" : ""}`}
              />
            </button>

            {/* Expanded pipeline steps */}
            {open && (
              <div className="mt-1 bg-slate-800 rounded-xl overflow-hidden shadow-lg shadow-slate-900/20 text-[13px]">
                <div className="px-4 py-3 space-y-3">
                  {/* Model & Level info */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-700 text-slate-300 text-[11px] font-mono">
                      {model}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-500/20 text-indigo-300 text-[11px] font-semibold">
                      {levelLabel}
                    </span>
                    {hasRlhf && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/20 text-amber-300 text-[11px] font-semibold">
                        Koreksi Expert Aktif
                      </span>
                    )}
                  </div>

                  {/* Step list */}
                  {steps.map((step, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-slate-300">
                      <span className="mt-0.5 text-slate-500"><StepIcon /></span>
                      <div className="flex-1 min-w-0">
                        <div>
                          <span className="text-slate-200 font-medium">{step.label}</span>
                          <span className="text-slate-500 text-[11px] ml-2 font-mono">
                            {formatMs(step.latency_ms)}
                          </span>
                          {(step.tokens_in > 0 || step.tokens_out > 0) && (
                            <span className="text-slate-600 text-[11px] ml-1.5 font-mono">
                              {step.tokens_in + step.tokens_out} tok
                            </span>
                          )}
                        </div>
                        {step.detail && (
                          <div className="text-[12px] text-slate-400 mt-1 whitespace-pre-wrap leading-[1.6]">
                            {step.detail}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer with totals */}
                <div className="px-4 py-2 bg-slate-900/50 border-t border-slate-700/50 flex items-center justify-between text-[11px] text-slate-500">
                  <span>
                    {totalTokens > 0 && `${totalTokens.toLocaleString()} tokens`}
                  </span>
                  <span className="font-mono font-medium text-slate-400">
                    Total: {formatMs(totalMs)}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
